import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2, Heart, Clock, CheckCircle, X } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { useSignedPhotoUrl } from "@/lib/photoUrl";

interface Match {
  id: string;
  status: string;
  created_at: string;
  profile_1_id: string;
  profile_2_id: string;
  profile_1_response?: string;
  profile_2_response?: string;
  other_profile?: {
    id: string;
    first_name?: string;
    age?: number;
    location?: string;
    profile_picture_url?: string;
  } | null;
}

export default function MatchesList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<any>(null);

  const fetchMatches = async () => {
    if (!user) return;

    try {
      setLoading(true);

      const { data: matchesData, error } = await supabase
        .from("matches")
        .select(`
          id,
          status,
          created_at,
          profile_1_id,
          profile_2_id,
          profile_1_response,
          profile_2_response,
          profile_1:profiles!matches_profile_1_id_fkey(
            id, first_name, age, location, profile_picture_url
          ),
          profile_2:profiles!matches_profile_2_id_fkey(
            id, first_name, age, location, profile_picture_url
          )
        `)
        .or(`profile_1_id.eq.${user.id},profile_2_id.eq.${user.id}`)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Transform data to include the other profile
      const transformedMatches = matchesData
        ?.map((match: any) => {
          const otherProfile = match.profile_1_id === user.id ? match.profile_2 : match.profile_1;

          // Log warning for matches with missing profiles but don't filter them out
          if (!otherProfile) {
            console.warn("matches_list.profile_missing");
          }

          return {
            id: match.id,
            status: match.status,
            created_at: match.created_at,
            profile_1_id: match.profile_1_id,
            profile_2_id: match.profile_2_id,
            profile_1_response: match.profile_1_response,
            profile_2_response: match.profile_2_response,
            other_profile: otherProfile || null,
          } as Match;
        }) || [];

      setMatches(transformedMatches);
    } catch (err) {
      console.error("matches_list.fetch_failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;
      
      const { data } = await supabase
        .from("profiles")
        .select("first_name, profile_picture_url")
        .eq("id", user.id)
        .maybeSingle();
      
      if (data) setUserProfile(data);
    };

    fetchUserProfile();
    fetchMatches();
  }, [user]);

  // Real-time subscription
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("matches-list-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "matches",
          filter: `profile_1_id=eq.${user.id},profile_2_id=eq.${user.id}`,
        },
        () => {
          fetchMatches();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const getMatchStatus = (match: Match) => {
    const isProfile1 = match.profile_1_id === user?.id;
    const myResponse = isProfile1 ? match.profile_1_response : match.profile_2_response;
    const theirResponse = isProfile1 ? match.profile_2_response : match.profile_1_response;

    if (match.status === "both_accepted") {
      return { label: "Both Accepted", variant: "default" as const, icon: CheckCircle };
    }
    if (match.status === "rejected") {
      return { label: "Declined", variant: "destructive" as const, icon: X };
    }
    if (myResponse === "accepted" && !theirResponse) {
      return { label: "Awaiting Response", variant: "secondary" as const, icon: Clock };
    }
    if (theirResponse === "accepted" && !myResponse) {
      return { label: "Action Required", variant: "default" as const, icon: Heart };
    }
    return { label: "Pending", variant: "outline" as const, icon: Clock };
  };

  const filterMatches = (filter: "all" | "pending" | "accepted") => {
    switch (filter) {
      case "pending":
        return matches.filter(m => m.status !== "both_accepted" && m.status !== "rejected");
      case "accepted":
        return matches.filter(m => m.status === "both_accepted");
      default:
        return matches;
    }
  };

  const MatchCard = ({ match }: { match: Match }) => {
    const status = getMatchStatus(match);
    const StatusIcon = status.icon;

    const hasProfile = !!match.other_profile;
    const firstName = match.other_profile?.first_name || "Unknown";
    const age = match.other_profile?.age || "?";
    const location = match.other_profile?.location || "Location unavailable";
    const avatarFallback = firstName.charAt(0).toUpperCase() || "?";
    const signedProfilePicture = useSignedPhotoUrl(
      match.other_profile?.profile_picture_url ?? undefined,
    );

    return (
      <Card
        className="cursor-pointer hover:border-primary/50 transition-colors"
        onClick={() => navigate(`/dashboard/matches/${match.id}`)}
      >
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage
                src={signedProfilePicture ?? undefined}
                alt={firstName}
              />
              <AvatarFallback className="text-lg">
                {avatarFallback}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 space-y-2">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-lg">
                    {firstName}, {age}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {location}
                  </p>
                  {!hasProfile && (
                    <Badge variant="destructive" className="mt-1">
                      Profile Unavailable
                    </Badge>
                  )}
                </div>
                <Badge variant={status.variant} className="flex items-center gap-1">
                  <StatusIcon className="h-3 w-3" />
                  {status.label}
                </Badge>
              </div>

              <p className="text-xs text-muted-foreground">
                Received {formatDistanceToNow(new Date(match.created_at), { addSuffix: true })}
              </p>

              <Button variant="ghost" size="sm" className="w-full">
                View Details →
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const MatchesGrid = ({ filter }: { filter: "all" | "pending" | "accepted" }) => {
    const filtered = filterMatches(filter);

    if (loading) {
      return (
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <Skeleton className="h-16 w-16 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-3 w-40" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      );
    }

    if (filtered.length === 0) {
      return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Heart className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              No {filter !== "all" && filter} matches found
            </p>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="grid gap-4">
        {filtered.map((match) => (
          <MatchCard key={match.id} match={match} />
        ))}
      </div>
    );
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <DashboardSidebar />
        
        <div className="flex-1 flex flex-col">
          <DashboardHeader
            title="My Matches"
            profilePicture={userProfile?.profile_picture_url}
            firstName={userProfile?.first_name}
          />

          <main className="flex-1 overflow-y-auto bg-gradient-to-br from-primary/5 via-background to-secondary/5">
            <div className="container max-w-4xl mx-auto p-6">
              {/* Welcome Banner */}
              <div className="mb-8 rounded-2xl bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 p-6 border border-primary/20 shadow-lg">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl">💖</span>
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                    Your Matches
                  </h2>
                </div>
                <p className="text-foreground/70">
                  Track your connections and see who's interested in getting to know you better! ✨
                </p>
              </div>

              <Tabs defaultValue="all" className="space-y-6">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="all">
                    All ({matches.length})
                  </TabsTrigger>
                  <TabsTrigger value="pending">
                    Pending ({filterMatches("pending").length})
                  </TabsTrigger>
                  <TabsTrigger value="accepted">
                    Accepted ({filterMatches("accepted").length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="all">
                  <MatchesGrid filter="all" />
                </TabsContent>

                <TabsContent value="pending">
                  <MatchesGrid filter="pending" />
                </TabsContent>

                <TabsContent value="accepted">
                  <MatchesGrid filter="accepted" />
                </TabsContent>
              </Tabs>
            </div>
          </main>
          <Footer />
        </div>
      </div>
    </SidebarProvider>
  );
}
